// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { MenuItem, TextField } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyBranchDetailPrice, TyProductDetail } from "@/types";
import InputGroups from "@/UI/components/InputGroups";
import ProductSizePopup from "@/UI/popups/ProductSizePopup";
import type { ProductSizePopupDetails, TyPriceRow } from "@/UI/popups/ProductSizePopup";
import { hasAnyLangValue, pickLangValue, readLangs, writeLangs } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type BranchProductSettingsPopupDetails = {
    productId: string;
    productName: string;
    branchId: string;
    existingEntry: TyBranchDetailPrice | null;
    onSuccess: (newEntry: {
        prices: Array<{ _id: string; amount: number; showLabel?: boolean }>;
        hiddenPrices: string[];
        discount: TyBranchDetailPrice["discount"];
    }) => void;
};

type Props = {
    onClose: () => void;
    details?: BranchProductSettingsPopupDetails;
};

type FormState = {
    prices: TyPriceRow[];
    hiddenPriceIds: string[];
    hasDiscount: boolean;
    discountType: "percentage" | "fixed";
    discountAmount: string;
};

// --- Helpers ------------------------------------------------------------------------------------

const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": { color: "var(--color-brand)" },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-full)",
        "& fieldset": { borderColor: "var(--color-gray-01)", borderWidth: "1.5px" },
        "& fieldset legend": {
            textAlign: dir === "rtl" ? "right" : "left",
            marginLeft: dir === "rtl" ? "auto" : undefined,
            marginRight: dir === "rtl" ? "0px" : undefined,
        },
        "&:hover fieldset": { borderColor: "var(--color-gray-02)" },
        "&.Mui-focused fieldset": { borderColor: "var(--color-brand)", borderWidth: "1.5px" },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-select": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px !important",
        paddingRight: dir === "rtl" ? "14px !important" : "32px !important",
        paddingLeft: dir === "rtl" ? "32px !important" : "14px !important",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiOutlinedInput-notchedOutline legend": { textAlign: dir === "rtl" ? "right" : "left" },
    "& .MuiSelect-icon": {
        right: dir === "rtl" ? "unset" : 7,
        left: dir === "rtl" ? 7 : "unset",
    },
});

// --- Logic --------------------------------------------------------------------------------------

export default function BranchProductSettingsPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { languages, defaultLang } = useStoreLanguages();
    const addPopup = usePopup((state) => state.addComp);

    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSx = getTextFieldSx(dir as "rtl" | "ltr");

    const [form, setForm] = useState<FormState>({
        prices: [],
        hiddenPriceIds: [],
        hasDiscount: false,
        discountType: "percentage",
        discountAmount: "",
    });

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((prev) => ({ ...prev, [key]: value }));

    const { data: detailData, loading: detailLoading } = useFetch<TyApiResponse<{ product: TyProductDetail }>>(
        `/menu/products/${details?.productId ?? "x"}`,
        {},
        details?.productId ? undefined : false
    );

    useEffect(() => {
        const product = detailData?.data?.product;
        if (!product) return;

        const existingEntry = details?.existingEntry;

        const prices: TyPriceRow[] = product.prices.map((p) => {
            const override = existingEntry?.prices?.find((ep) => ep._id === p._id);
            return {
                key: crypto.randomUUID(),
                originalId: p._id,
                amount: override !== undefined ? String(override.amount) : String(p.amount),
                label: readLangs(p.label, languages),
                showLabel: override?.showLabel !== undefined ? override.showLabel : p.showLabel,
                hasDiscount: p.discount?.active ?? false,
                discountType: p.discount?.type ?? "percentage",
                discountAmount: String(p.discount?.active ? p.discount.amount : ""),
            };
        });

        const discount = existingEntry?.discount ?? product.discount;

        setForm({
            prices,
            hiddenPriceIds: existingEntry?.hiddenPrices ?? [],
            hasDiscount: discount?.active ?? false,
            discountType: discount?.type ?? "percentage",
            discountAmount: String(discount?.active ? discount.amount : ""),
        });
    }, [detailData]);

    const {
        data: submitData,
        loading: submitting,
        error: submitError,
        invoke: submit,
    } = useFetch<TyApiResponse<unknown>>(`/branches/products/${details?.productId ?? "x"}`, { method: "PUT" }, false);

    useEffect(() => {
        if (!submitData?.success) return;

        const newPrices = form.prices
            .map((p) => ({ _id: p.originalId ?? "", amount: parseFloat(p.amount) || 0, showLabel: p.showLabel }))
            .filter((p) => p._id);

        const newDiscount = {
            active: form.hasDiscount && Boolean(form.discountAmount),
            type: form.discountType,
            amount: form.hasDiscount && form.discountAmount ? parseFloat(form.discountAmount) || 0 : 0,
        };

        details?.onSuccess({ prices: newPrices, hiddenPrices: form.hiddenPriceIds, discount: newDiscount });
        onClose();
    }, [submitData]);

    // --------------------------------------------------------------------------------------------

    const toggleHidePrice = (originalId: string) => {
        setForm((prev) => ({
            ...prev,
            hiddenPriceIds: prev.hiddenPriceIds.includes(originalId)
                ? prev.hiddenPriceIds.filter((id) => id !== originalId)
                : [...prev.hiddenPriceIds, originalId],
        }));
    };

    const openPricePopup = (index: number) => {
        const initialPrice = form.prices[index];
        const popupDetails: ProductSizePopupDetails = {
            initialPrice,
            hideLabelInputs: true,
            onSave: (saved: TyPriceRow) => {
                setForm((prev) => ({
                    ...prev,
                    prices: prev.prices.map((p, i) => (i === index ? { ...saved, originalId: p.originalId } : p)),
                }));
            },
        };
        addPopup({ name: "product-price-popup", Component: ProductSizePopup, details: popupDetails, anim: "scale" });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const prices = form.prices.map((p) => ({
            ...(p.originalId ? { _id: p.originalId } : {}),
            amount: parseFloat(p.amount) || 0,
            label: [...writeLangs(p.label, languages, defaultLang)],
            showLabel: p.showLabel,
            discount:
                p.hasDiscount && p.discountAmount
                    ? { active: true, type: p.discountType, amount: parseFloat(p.discountAmount) || 0 }
                    : { active: false, type: p.discountType, amount: 0 },
        }));

        const discount = {
            active: form.hasDiscount && Boolean(form.discountAmount),
            type: form.discountType,
            amount: form.hasDiscount && form.discountAmount ? parseFloat(form.discountAmount) || 0 : 0,
        };

        submit({ data: { branchId: details?.branchId, prices, hiddenPrices: form.hiddenPriceIds, discount } });
    };

    const isSubmitDisabled = submitting || detailLoading;

    return (
        <div className="bpsp">
            <div className="bpsp-header">
                <div className="bpsp-header-text">
                    <p className="bpsp-title">{t.branchMenu.settingsTitle}</p>
                    <p className="bpsp-subtitle">{details?.productName}</p>
                </div>
                <button type="button" className="bpsp-close" onClick={onClose} aria-label="Close">
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            <form className="bpsp-form" onSubmit={handleSubmit} noValidate>
                <div className="bpsp-fields">
                    {/* ── Prices ── */}
                    <InputGroups icon="/icons/inputs-group-icon.svg" title={t.formProduct.pricesSection}>
                        {detailLoading ? (
                            <div className="bpsp-skeleton-list">
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <div key={i} className="bpsp-skeleton-row" />
                                ))}
                            </div>
                        ) : form.prices.length === 0 ? (
                            <div className="bpsp-empty">{t.formProduct.emptyPrices}</div>
                        ) : (
                            <div className="bpsp-price-list">
                                {form.prices.map((price, index) => {
                                    const isHidden = price.originalId ? form.hiddenPriceIds.includes(price.originalId) : false;
                                    const originalProductPrice = detailData?.data?.product?.prices.find((p) => p._id === price.originalId);
                                    const originalAmount = originalProductPrice ? String(originalProductPrice.amount) : null;
                                    const hasOverride = originalAmount !== null && originalAmount !== price.amount;
                                    return (
                                        <div key={price.key} className={`bpsp-price-card${isHidden ? " bpsp-price-card--hidden" : ""}`}>
                                            <div className="bpsp-price-card-info">
                                                {price.showLabel && hasAnyLangValue(price.label) && (
                                                    <span className="bpsp-price-card-label">{pickLangValue(price.label, lang ?? defaultLang)}</span>
                                                )}
                                                {hasOverride ? (
                                                    <span className="bpsp-price-card-amounts">
                                                        <span className="bpsp-price-card-amount bpsp-price-card-amount--original">
                                                            {originalAmount}
                                                        </span>
                                                        <span className="bpsp-price-card-amount">{price.amount}</span>
                                                    </span>
                                                ) : (
                                                    <span className="bpsp-price-card-amount">{price.amount || "0"}</span>
                                                )}
                                            </div>
                                            <div className="bpsp-price-card-actions">
                                                {isHidden && <span className="bpsp-hidden-badge">{t.branchMenu.priceHiddenBadge}</span>}
                                                <button
                                                    type="button"
                                                    className="bpsp-price-btn bpsp-price-btn--edit"
                                                    onClick={() => openPricePopup(index)}
                                                    disabled={isHidden}
                                                >
                                                    <img src="/icons/edit-icon.svg" alt="" width={14} height={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`bpsp-price-btn bpsp-price-btn--hide${isHidden ? " bpsp-price-btn--hide-active" : ""}`}
                                                    onClick={() => price.originalId && toggleHidePrice(price.originalId)}
                                                >
                                                    <img src="/icons/delete-icon.svg" alt="" width={14} height={14} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </InputGroups>

                    {/* ── Discount ── */}
                    <InputGroups
                        icon="/icons/inputs-group-icon.svg"
                        title={t.formProduct.discountSection}
                        toggle={{ checked: form.hasDiscount, onChange: (v) => setField("hasDiscount", v) }}
                    >
                        {form.hasDiscount && (
                            <div className="bpsp-row">
                                <TextField
                                    select
                                    fullWidth
                                    label={t.formProduct.discountType}
                                    value={form.discountType}
                                    onChange={(e) => setField("discountType", e.target.value as "percentage" | "fixed")}
                                    sx={textFieldSx}
                                >
                                    <MenuItem value="percentage">{t.formProduct.discountPercentage}</MenuItem>
                                    <MenuItem value="fixed">{t.formProduct.discountFixed}</MenuItem>
                                </TextField>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t.formProduct.discountAmount}
                                    value={form.discountAmount}
                                    onChange={(e) => setField("discountAmount", e.target.value)}
                                    slotProps={{ htmlInput: { min: 0 } }}
                                    sx={textFieldSx}
                                />
                            </div>
                        )}
                    </InputGroups>
                </div>

                {submitError && <p className="bpsp-error">{submitError}</p>}

                <div className="bpsp-footer">
                    <button type="button" className="bpsp-btn bpsp-btn--cancel" onClick={onClose} disabled={submitting}>
                        {t.branchMenu.cancelLabel}
                    </button>
                    <button type="submit" className="bpsp-btn bpsp-btn--submit" disabled={isSubmitDisabled}>
                        {submitting ? <span className="bpsp-spinner" /> : t.branchMenu.saveLabel}
                    </button>
                </div>
            </form>
        </div>
    );
}
